import React, { useCallback, useEffect, useMemo, useState } from "react";
import { validator } from "../utils/validator";
import TextField from "../components/common/form/TextField";
import RadioField from "../components/common/form/RadioField";
import CheckBoxField from "../components/common/form/CheckBoxField";
import { useDispatch, useSelector } from "react-redux";
import SelectField from "../components/common/form/SelectField";
import { getSpecialties } from "../store/specialties";
import { getAuthErrors, signUp } from "../store/users";
import { authErrorText } from "../utils/generateAuthError";
import { getLevelOne } from "../store/levels";
import { useNavigate, useParams } from "react-router-dom";
import CaptchaComponent from "../components/CaptchaComponent";
import { getTowns } from "../store/towns";
import DateField from "../components/common/form/DateField";
import { useI18n } from "../i18n/LanguageContext";

const RegisterForm = () => {
  const dispatch = useDispatch();
  const { country } = useParams();

  const level = useSelector(getLevelOne());
  const towns = useSelector(getTowns());
  const authError = useSelector(getAuthErrors());
  const { t } = useI18n();

  const navigate = useNavigate();

  //server error 500 on levels
  const [data, setData] = useState({
    email: "",
    password: "",
    nickname: "",
    birthDate: "",
    phoneNumber: "",
    teamId: "",
    level: level?._id || "",
    sex: "male",
    skill: [],
    specialty: "",
    license: false,
    stayOn: false,
    town: "",
  });

  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showAuthError, setShowAuthError] = useState(false);
  const specialties = useSelector(getSpecialties());
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (level?._id && !data.level) {
      setData((prev) => ({
        ...prev,
        level: level._id,
      }));
    }
  }, [level, data.level]);

  const selectSpecialties = useMemo(
    () =>
      (Array.isArray(specialties) ? specialties : []).map((elem) => ({
        value: elem._id,
        label: elem.name,
      })),
    [specialties]
  );

  const setSelectTown = useMemo(
    () =>
      (Array.isArray(towns) ? towns : []).map((elem) => ({
        value: elem._id,
        label: elem.name,
      })),
    [towns]
  );

  const handleChange = (target) => {
    setData((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setTouched((prev) => ({ ...prev, [target.name]: true }));
  };

  const handleOpen = (e) => {
    e.preventDefault();
    setIsOpen((prev) => !prev);
  };

  const validatorConfig = useMemo(
    () => ({
      email: {
        isRequired: {
          message: t("login.err.emailRequired"),
        },
        isEmail: {
          message: t("login.err.email"),
        },
        max: {
          message: t("login.err.emailMax"),
          value: 32,
        },
      },
      nickname: {
        isRequired: {
          message: t("register.err.nickRequired"),
        },
        isContainDigitEndLetter: {
          message: t("register.err.nickChars"),
        },
        min: {
          message: t("register.err.nickMin"),
          value: 3,
        },
        max: {
          message: t("register.err.nickMax"),
          value: 15,
        },
      },
      birthDate: {
        isRequired: {
          message: t("register.err.birth"),
        },
      },
      phoneNumber: {
        isRequired: {
          message: t("register.err.phoneRequired"),
        },
        min: {
          message: t("register.err.phoneMin"),
          value: 10,
        },
        max: {
          message: t("register.err.phoneMax"),
          value: 15,
        },
      },
      password: {
        isRequired: {
          message: t("login.err.passwordRequired"),
        },
        isCapitalSymbol: {
          message: t("login.err.capital"),
        },
        isContainDigit: {
          message: t("login.err.digit"),
        },
        min: {
          message: t("login.err.passwordMin"),
          value: 8,
        },
        max: {
          message: t("login.err.passwordMax"),
          value: 25,
        },
      },
      specialty: {
        isRequired: {
          message: t("register.err.specialty"),
        },
      },
      license: {
        isRequired: {
          message: t("register.err.license"),
        },
      },
      stayOn: {
        isRequired: {
          message: t("login.err.captcha"),
          value: true,
        },
      },
      town: {
        isRequiredTown: {
          message: t("register.err.town"),
        },
      },
    }),
    [t]
  );

  const validate = useCallback(() => {
    const errors = validator(data, validatorConfig);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }, [data, validatorConfig]);

  useEffect(() => {
    validate();
  }, [validate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const allTouched = Object.keys(data).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    const formIsValid = validate();
    if (!formIsValid) return;
    if (!data.level) {
      setSubmitError(t("register.levelWait"));
      return;
    }

    setSubmitError("");
    setSubmitting(true);
    const registered = await dispatch(
      signUp({
        email: data.email,
        password: data.password,
        nickname: data.nickname,
        birthDate: data.birthDate,
        phoneNumber: data.phoneNumber,
        teamId: data.teamId,
        level: data.level,
        sex: data.sex,
        skill: data.skill,
        specialty: data.specialty,
        town: data.town,
        license: data.license,
      })
    );
    if (registered) {
      navigate(`/${country}`);
      return;
    }
    setShowAuthError(true);
    setSubmitting(false);
  };

  const handleCaptcha = () => {
    setData((prevState) => ({
      ...prevState,
      stayOn: true,
    }));
  };

  // Показываем ошибки только для touched полей
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-full">
      <TextField
        label={t("register.email")}
        name="email"
        type="email"
        value={data.email}
        onChange={handleChange}
        error={getFieldError("email")}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        placeholder="go888@gmail.com"
      />
      <TextField
        label={t("register.password")}
        type="password"
        name="password"
        value={data.password}
        onChange={handleChange}
        error={getFieldError("password")}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
      />
      <TextField
        label={t("register.nickname")}
        name="nickname"
        value={data.nickname}
        onChange={handleChange}
        error={getFieldError("nickname")}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        placeholder="GGG"
      />
      <DateField
        label={t("register.birth")}
        name="birthDate"
        value={data.birthDate}
        onChange={handleChange}
        error={getFieldError("birthDate")}
        max={new Date().toISOString().split("T")[0]} // запрещает будущее
      />

      <TextField
        label={t("register.phone")}
        name="phoneNumber"
        value={data.phoneNumber}
        onChange={handleChange}
        error={getFieldError("phoneNumber")}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        placeholder="+77758889966"
      />
      <SelectField
        label={t("register.specialty")}
        defaultOption={t("register.choose")}
        options={selectSpecialties}
        name="specialty"
        onChange={handleChange}
        value={data.specialty}
        error={getFieldError("specialty")}
      />
      <SelectField
        label={t("register.town")}
        defaultOption={t("register.choose")}
        options={setSelectTown}
        name="town"
        onChange={handleChange}
        value={data.town}
        error={getFieldError("town")}
      />
      <RadioField
        options={[
          { name: t("register.male"), value: "male" },
          { name: t("register.female"), value: "female" },
        ]}
        value={data.sex}
        name="sex"
        onChange={handleChange}
        label={t("register.sex")}
      />
      <div className="bg-slate-200 mt-5">
        <button
          className="btn-smooth w-full rounded-md bg-green-200 px-5 py-2 hover:bg-green-300"
          type="button"
          onClick={handleOpen}
        >
          {isOpen ? t("register.hideAgreement") : t("register.showAgreement")}
        </button>
        {isOpen && (
          <div className="p-5">
            <h2 className="text-center font-bold text-lg mb-4">
              {t("register.agreementTitle")}
            </h2>
            <div className="text-sm space-y-2 max-h-96 overflow-y-auto">
              {t("register.agreementIntro")}
              {Array.from({ length: 20 }, (_, index) => (
                <p key={index}>
                  {index + 1}. {t(`register.a${index + 1}`)}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      <CheckBoxField
        value={data.license}
        onChange={handleChange}
        name="license"
        error={getFieldError("license")}
      >
        {t("register.agree")}
      </CheckBoxField>

      <div className="flex flex-col w-full justify-center">
        <CaptchaComponent
          onChange={handleCaptcha}
          error={getFieldError("stayOn")}
        />
      </div>
      {submitError || (showAuthError && authError) ? (
        <p className="text-red-500 text-sm px-3 py-2 mt-4 bg-red-50 rounded border border-red-200">
          {submitError ||
            (typeof authError === "string"
              ? authErrorText(t, authError)
              : t("register.fail"))}
        </p>
      ) : null}
      <div className="flex justify-end mt-5">
        <button
          type="submit"
          value="Submit"
          className="btn-smooth mb-2 mr-2 rounded-lg bg-lime-500 px-5 py-2.5 text-sm font-medium text-black hover:bg-lime-400 focus:outline-none focus:ring-4 focus:ring-lime-300 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={submitting}
        >
          {submitting ? t("register.submitting") : t("register.submit")}
        </button>
      </div>
    </form>
  );
};

export default RegisterForm;
